import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useContext,
} from "react";
import { DocumentNode, useApolloClient, FetchPolicy } from "@apollo/client";
import { AgGridReact } from "ag-grid-react";
import {
  GridApi,
  ColDef,
  IDatasource,
  RowSelectedEvent,
  CellClickedEvent,
  IRowNode,
  RowClickedEvent,
  AllCommunityModule,
  ModuleRegistry,
} from "ag-grid-community";
import { Box, Typography } from "@mui/material";
import { XCellRenderer } from "./XCellRenderer";
import { ColumnHeader } from "./ColumnHeader";
import { InfiniteHeaderPanel } from "./InfiniteHeaderPanel";
import { LoadingOverlay } from "./LoadingOverlay";
import { NoRowsOverlay } from "./NoRowsOverlay";
import { EnvironmentContext } from "../../../app/utils/EnvironmentProvider";
import "./override.scss";

ModuleRegistry.registerModules([AllCommunityModule]);

const AGInfiniteScrollTable = ({
  label,
  fetchQuery,
  defaultColDef,
  columnDefs,
  rowSelection = "singleRow",
  fetchCount = 50,
  headerPanelHeight = 56,
  variableName = "query",
  extraQueryParams,
  initialOrderBy,
  initialOrderDirection = "asc",
  initialSearchText,
  fetchPolicy = "network-only",
  rowHeight,
  isRowSelectable,
  disableColumnsSetting,
  disableSearch,
  multiSort = false,
  onCellClicked,
  onRowClicked,
  onRowSelected,
  actionBarRenderer,
  customHeaderPanelRenderer,
  customTitleBoxRenderer,
  responseConvertFn,
}: {
  label?: string;
  customSearchText?: string;
  fetchQuery: DocumentNode;
  defaultColDef?: ColDef;
  columnDefs?: ColDef[];
  initialSearchText?: string;
  rowSelection?: "singleRow" | "multiRow";
  fetchCount?: number;
  headerPanelHeight?: number;
  extraQueryParams?: Record<string, string | undefined>;
  initialOrderBy?: string;
  initialOrderDirection?: "asc" | "desc" | null;
  fetchPolicy?: FetchPolicy;
  rowHeight?: number;
  disableColumnsSetting?: boolean;
  disableSearch?: boolean;
  multiSort?: boolean;
  isRowSelectable?: (params: IRowNode<any>) => boolean;
  onCellClicked?: (event: CellClickedEvent<any, any>) => void;
  onRowClicked?: (event: RowClickedEvent<any>) => void;
  onRowSelected?: (event: RowSelectedEvent<any>) => void;
  actionBarRenderer?: (gridApi: GridApi) => React.ReactNode;
  customHeaderPanelRenderer?: (
    gridApi: GridApi,
    internalOption: {
      flexColDefs: ColDef<any>[];
      token?: string;
      totalRecords: number;
      loading?: boolean;
    },
    onSearch: (searchText: string) => void
  ) => React.ReactNode;
  customTitleBoxRenderer?: () => React.ReactNode;
  responseConvertFn: (response: any) => {
    columns?: ColDef<any>[];
    rows: any[];
    totalResults: number; // !important: this should be number, if not, it will work wrongly
    token?: string;
    emptyDescription?: string;
  };
  variableName?: string;
}) => {
  const client = useApolloClient();
  const gridRef = useRef<any>();
  const environment = useContext(EnvironmentContext);

  const containerStyle: any = useMemo(
    () => ({
      width: "100%",
      height: `calc(100% - ${headerPanelHeight}px)`,
      position: "relative",
    }),
    [headerPanelHeight]
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);

  const [gridApi, setGridApi] = useState<GridApi | undefined>();

  const [internalOption, setInternalOption] = useState<{
    flexColDefs: ColDef<any>[];
    token?: string;
    totalRecords: number;
    loading?: boolean;
  }>({
    flexColDefs: [],
    totalRecords: 0,
    loading: false,
  });

  const [searchText, setSearchText] = useState("");
  const [sorting, setSorting] = useState<{
    order: "asc" | "desc" | null;
    orderBy: string | null | undefined;
  }>({
    order: initialOrderDirection,
    orderBy: initialOrderBy
      ? initialOrderBy
      : columnDefs?.length
      ? columnDefs[0].field
      : null,
  });

  const handleDownload = () => {
    const query = new URLSearchParams();
    query.append('index', 'swimlane_cases');
    query.append('filter_value', searchText.trim() || '*');
    if (sorting.orderBy) {
      query.append('sort_by', sorting.orderBy);
      if (sorting.order) {
        query.append('order', sorting.order);
      }
    }
    if (extraQueryParams) {
      for (const key in extraQueryParams) {
        if (extraQueryParams[key]) {
          query.append(key, extraQueryParams[key] as string);
        }
      }
    }
    const downloadUrl = `${environment.EIP_FILE_DOWNLOAD}get-csv-file?${query.toString()}`;
    window.open(downloadUrl, '_blank');
  };

  const dataSource = useMemo<IDatasource>(
    () => ({
      getRows: (params) => {
        console.log("asking for " + params.startRow + " to " + params.endRow);
        const nextPage = params.endRow / fetchCount;
        setInternalOption((prev) => ({ ...prev, loading: true }));
        client
          .query({
            query: fetchQuery,
            variables: {
              [variableName]: [searchText.trim() || "*", fetchCount, nextPage],
              ...(multiSort
                ? {
                    multisort: [
                      {
                        sortBy: sorting.orderBy,
                        asc: sorting.order === "desc",
                      },
                    ],
                  }
                : {
                    sort: sorting.orderBy,
                    desc:
                      sorting.order === "desc"
                        ? true
                        : sorting.order === "asc"
                        ? false
                        : null,
                    ...(!!extraQueryParams && { ...extraQueryParams }),
                  }),
            },
            fetchPolicy,
          })
          .then((response: any) => {
            const table = responseConvertFn(response.data);
            console.log("###table", table);
            setInternalOption({
              flexColDefs: !columnDefs?.length ? table.columns! : [],
              token: table.token,
              totalRecords: +table.totalResults,
              loading: false,
            });
            let lastRow = -1;
            if (table.totalResults <= params.endRow) {
              lastRow = table.totalResults;
            }
            // call the success callback
            params.successCallback(table.rows, lastRow);
          });
      },
    }),
    [
      client,
      sorting,
      searchText,
      fetchPolicy,
      extraQueryParams,
      fetchCount,
      fetchQuery,
      variableName,
      columnDefs,
      multiSort,
      responseConvertFn,
    ]
  );

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const onSort = useCallback(
    (columnId: string, order: "asc" | "desc" | null) => {
      setSorting({
        order,
        orderBy: columnId,
      });
    },
    []
  );

  const onSearch = useCallback((search: string) => {
    setSearchText(search);
  }, []);

  const components = useMemo(
    () => ({
      agColumnHeader: ColumnHeader,
    }),
    []
  );

  const refinedColumnDefs = useMemo(
    () => [
      ...(columnDefs?.length ? columnDefs : internalOption.flexColDefs).map(
        (colDef) => ({
          ...colDef,
          cellRenderer: colDef.cellRenderer ?? XCellRenderer,
          // ...(!colDef.valueFormatter && { cellRenderer: XCellRenderer }),
        })
      ),
    ],
    [columnDefs, internalOption.flexColDefs]
  );

  const refinedDefaultColDef = useMemo<ColDef>(
    () => ({
      flex: 1,
      // resizable: true,
      // autoHeight: true,
      // wrapHeaderText: true,
      autoHeaderHeight: true,
      headerComponentParams: { sorting, onSort },
      ...(!!defaultColDef && { ...defaultColDef }),
    }),
    [defaultColDef, sorting, onSort]
  );

  useEffect(() => {
    setSearchText(initialSearchText || "*");
  }, [initialSearchText]);

  useEffect(() => {
    setSorting({
      order: initialOrderDirection,
      orderBy: initialOrderBy ? initialOrderBy : null,
    });
  }, [initialOrderDirection, initialOrderBy]);

  useEffect(() => {
    if (!gridApi) return;
    const timeout = setTimeout(() => {
      gridApi.setGridOption("datasource", dataSource);
    }, 0);
    return () => {
      clearTimeout(timeout);
    };
  }, [gridApi, dataSource]);

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      {gridApi && refinedColumnDefs.length > 0 ? (
        !!customHeaderPanelRenderer ? (
          customHeaderPanelRenderer(gridApi, internalOption, onSearch)
        ) : (
          <Box
            sx={{
              height: headerPanelHeight,
              p: 1,
              borderLeft: "1px solid",
              borderRight: "1px solid",
              borderTop: "1px solid",
              borderColor: "rgba(255, 255, 255, 0.25)",
              bgcolor: "primary.main",
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
              display: "flex",
              alignItems: !!customTitleBoxRenderer ? "start" : "center",
              gap: 2,
              overflowX: "auto",
              overflowY: "hidden",
            }}
          >
            {!!label && (
              <Typography variant="body1" fontWeight="bold">
                {label}
              </Typography>
            )}
            {!!customTitleBoxRenderer ? (
              customTitleBoxRenderer()
            ) : (
              <Typography
                variant="body1"
                fontSize={13}
                ml={1}
                whiteSpace="nowrap"
              >
                {internalOption.totalRecords.toLocaleString()} Records
              </Typography>
            )}
            {!!actionBarRenderer && actionBarRenderer(gridApi)}
            <Box mr="auto" />
            <InfiniteHeaderPanel
              gridApi={gridApi}
              disableColumnsSetting={disableColumnsSetting}
              initialSearchText={initialSearchText}
              onSearch={!disableSearch ? onSearch : undefined}
              token={internalOption.token}
            />
          </Box>
        )
      ) : (
        <></>
      )}
      <div style={containerStyle}>
        {internalOption.loading && <LoadingOverlay />}
        {!internalOption.loading && !internalOption.totalRecords && (
          <NoRowsOverlay />
        )}
        <div style={gridStyle} className="ag-theme-alpine-dark">
          <AgGridReact
            ref={gridRef}
            gridOptions={
              {
                // datasource: dataSource,
                // rowSelection: {
                //   mode: rowSelection,
                //   // enableClickSelection: true,
                //   isRowSelectable,
                // },
              }
            }
            columnDefs={refinedColumnDefs}
            defaultColDef={refinedDefaultColDef}
            rowBuffer={0}
            components={components}
            rowModelType={"infinite"}
            cacheBlockSize={fetchCount}
            cacheOverflowSize={2}
            maxConcurrentDatasourceRequests={1}
            infiniteInitialRowCount={fetchCount}
            rowHeight={rowHeight}
            maxBlocksInCache={10}
            onGridReady={onGridReady}
            onRowClicked={onRowClicked}
            onRowSelected={onRowSelected}
            onCellClicked={onCellClicked}
          />
        </div>
      </div>
    </Box>
  );
};

export default memo(AGInfiniteScrollTable);